import React from 'react';

import TextArea from 'react-textarea-autosize';
import { isMatchingTraceId } from '@this/src/util/string/string.util';
import type OrderItem from '@this/domain/order_item';
import type WifiElement from '../../../../../../domain/wifi_element';
import DatetimePicker from '../../../../../shared/datetime_picker/datetime_picker';
import { TransportElementAttachFileForm } from '../transport_element_form/transport_element_attach_file_form';

interface Props {
  element: WifiElement;
  orderItem: OrderItem;
  classNameForModifiedField: (...path: (string | number)[]) => string;
}

const WifiElementForm = ({ element, orderItem, classNameForModifiedField }: Props) => {
  const handleUploadFileChange = (file: File, elementId: number, travelerInformationId: string | null) => {
    if (!file) return;
    convertFileToBase64(file)
      .then(base64String => {
        element.handleUploadFileData(
          {
            upload_file_name: file.name,
            content_type: file.type,
            traveler_information_id: travelerInformationId,
            upload_file_base64: base64String
          },
          elementId,
          travelerInformationId
        );
      })
      .catch(error => {
        console.error('Failed to convert file to Base64', error);
      });
  };

  const handleDeleteFile = (elementId: number, travelerInformationId: string | null, index: number) => {
    element.handleDeleteFile(elementId, travelerInformationId, index);
  };

  const convertFileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const fileReader = new FileReader();
      fileReader.readAsDataURL(file);
      fileReader.onload = () => {
        const base64String = fileReader.result?.toString().replace(/^data:(.*);base64,/, '') || '';
        resolve(base64String);
      };
      fileReader.onerror = error => {
        reject(error);
      };
    });
  };

  return (
    <>
      <div className="virtual-counte-price-change-form__input-wrapper">
        <label className={`virtual-counte-price-change-form__label ${classNameForModifiedField('provider')}`}>
          プロバイダ
        </label>
        <select value={element.provider} onChange={e => element.handleChangeProvider(e.target.value)}>
          {Object.keys(element.wifiProviderOptions).map(v => (
            <option key={v} value={v}>
              {element.wifiProviderOptions[v]}
            </option>
          ))}
        </select>
      </div>
      <div className="virtual-counte-price-change-form__input-wrapper-item">
        <label className={`virtual-counte-price-change-form__label ${classNameForModifiedField('startedAt')}`}>
          利用開始日時
        </label>
        <DatetimePicker
          value={element.startedAt}
          onChange={time => element.handleStartedAtChange(time)}
          disabledDays={0}
          showTime
          showPast
        />
      </div>
      <div className="virtual-counte-price-change-form__input-wrapper-item">
        <label className={`virtual-counte-price-change-form__label ${classNameForModifiedField('endedAt')}`}>
          利用終了日時
        </label>
        <DatetimePicker
          value={element.endedAt}
          onChange={time => element.handleEndedAtChange(time)}
          disabledDays={0}
          showTime
          showPast
        />
      </div>
      <div className="virtual-counte-price-change-form__input-wrapper">
        <label className={`virtual-counte-price-change-form__label ${classNameForModifiedField('area')}`}>
          エリア
          <span className="red">（必須）</span>
        </label>
        <input type="text" value={element.area} onChange={e => element.handleChangeArea(e.target.value)} />
      </div>
      <div className="virtual-counte-price-change-form__input-wrapper">
        <label className={`virtual-counte-price-change-form__label ${classNameForModifiedField('remark')}`}>
          備考
        </label>
        <TextArea
          type="text"
          value={element.remark}
          onChange={e => element.handleChangeRemark(e.target.value)}
          minRows={2}
        />
      </div>
      <div className="virtual-counte-price-change-form__input-wrapper">
        <label
          className={`virtual-counte-price-change-form__label ${classNameForModifiedField('reservationNumber')}`}
        >
          注文番号
        </label>
        <input
          type="text"
          value={element.reservationNumber}
          onChange={e => element.handleReservationNumberChange(e.target.value)}
          style={{
            backgroundColor: isMatchingTraceId(element.reservationNumber, orderItem.traceId)
              ? '#ffebee'
              : undefined,
            borderColor: isMatchingTraceId(element.reservationNumber, orderItem.traceId) ? '#f8bbd9' : undefined
          }}
        />
      </div>
      <div className="virtual-counte-price-change-form__input-wrapper">
        <TransportElementAttachFileForm
          attachFiles={element.attachFiles}
          travelerInformations={orderItem.travelerInformations}
          element={element}
          attachableType={element.type}
          attachableId={element.id ?? 0}
          onChange={(file, elementId, travelerInformationId) =>
            handleUploadFileChange(file, element.id || 0, travelerInformationId)
          }
          onDelete={(elementId, travelerInformationId, index) =>
            handleDeleteFile(element.id!, travelerInformationId, index)
          }
        />
      </div>
    </>
  );
};

export default WifiElementForm;
